import { Button, Icon } from '@wtfalch/design';
import type { DraftAttachment, MailClient } from '@wtfalch/email/mailbox';
import { attach } from '@wtfalch/email/mailbox';
import { useCallback, useRef, useState } from 'react';

/** Files staged on a draft: upload with `attach()` as each is picked, hold
 *  the `DraftAttachment`s `send()` wants, and let one be taken back off
 *  before it goes. Uploaded eagerly rather than on send, so a large file's
 *  wait happens while a person is still writing rather than after they have
 *  pressed Send and are waiting on that too. */
export function useAttachments(client: MailClient, initial: readonly DraftAttachment[] = []) {
  const [attachments, setAttachments] = useState<DraftAttachment[]>([...initial]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const add = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    setError('');
    try {
      const added = await Promise.all(Array.from(files).map((file) => attach(client, file)));
      setAttachments((prev) => [...prev, ...added]);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = (blobId: string) => {
    setAttachments((prev) => prev.filter((a) => a.blobId !== blobId));
  };

  const clear = useCallback(() => setAttachments([]), []);

  return { attachments, add, remove, clear, busy, error };
}

export function AttachButton({
  onFiles,
  disabled,
}: {
  onFiles: (files: FileList | null) => void;
  disabled?: boolean;
}) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={ref}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          onFiles(e.target.files);
          e.target.value = '';
        }}
      />
      <Button
        kind="ghost"
        size="sm"
        iconOnly
        aria-label="Attach a file"
        disabled={disabled}
        onPress={() => ref.current?.click()}
      >
        <Icon name="paperclip" size={18} />
      </Button>
    </>
  );
}

export function AttachmentChips({
  attachments,
  onRemove,
  uploading,
}: {
  attachments: readonly DraftAttachment[];
  onRemove: (blobId: string) => void;
  /** A file is still going up: drawn as a chip of its own. */
  uploading?: boolean;
}) {
  if (attachments.length === 0 && !uploading) return null;
  return (
    <div className="flex items-center flex-wrap gap-2 pt-2">
      {attachments.map((a) => (
        <span key={a.blobId} className="mail-file">
          <Icon name="file" size={15} />
          <span>{a.name}</span>
          <button
            type="button"
            className="inline-flex text-muted"
            aria-label={`Remove ${a.name}`}
            onClick={() => onRemove(a.blobId)}
          >
            <Icon name="close" size={13} />
          </button>
        </span>
      ))}
      {uploading && (
        <span className="mail-file text-muted" role="status">
          Uploading…
        </span>
      )}
    </div>
  );
}
